import { useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { useField } from "../FieldContext";
import { Mono, Btn } from "../ui";
import { getPosition } from "../lib/geo";

export default function Swms() {
  const { store, version, bump, sync } = useField();
  const [swms, setSwms] = useState([]);
  const [sel, setSel] = useState({});
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    store.getSwms().then((s) => setSwms((s || []).sort((a, b) => (b.high_risk ? 1 : 0) - (a.high_risk ? 1 : 0))));
  }, [store, version]);

  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const chosen = Object.keys(sel).filter((k) => sel[k]);

  const acknowledge = async () => {
    setBusy(true);
    let pos = {};
    try {
      pos = await getPosition();
    } catch (e) {
      /* GPS optional for ack */
    }
    await store.queueOp("swms_ack", {
      swms_ids: chosen,
      context: "capture",
      latitude: pos.latitude ?? null,
      longitude: pos.longitude ?? null,
    });
    setBusy(false);
    setDone(true);
    setSel({});
    bump();
    sync();
    setTimeout(() => setDone(false), 4500);
  };

  return (
    <div className="space-y-5" data-testid="field-swms">
      <div>
        <Mono className="text-gold-400 text-[0.6rem]">Work health &amp; safety</Mono>
        <h1 className="font-heading text-2xl mt-1">SWMS</h1>
        <p className="text-white/50 text-sm mt-1 font-light">Review the relevant Safe Work Method Statements and acknowledge before starting on-site work.</p>
      </div>

      {done && (
        <div className="flex items-center gap-2 text-emerald-300 text-sm border border-emerald-500/30 bg-emerald-500/10 rounded-sm px-3 py-2.5" data-testid="field-swms-acked">
          <CheckCircle2 className="h-4 w-4" /> Acknowledgement recorded. It will sync to The MDAu Office.
        </div>
      )}

      {swms.length === 0 ? (
        <div className="border border-dashed border-gold-500/25 rounded-sm p-8 text-center text-white/45 text-sm" data-testid="field-swms-empty">No SWMS available yet.</div>
      ) : (
        <div className="space-y-2">
          {swms.map((s) => (
            <div key={s.id} data-testid={`field-swms-${s.id}`} className={`border rounded-sm ${sel[s.id] ? "border-gold-500/60 bg-gold-500/5" : "border-gold-500/20 bg-navy-900/40"}`}>
              <div className="flex items-start gap-3 p-3.5">
                <input type="checkbox" checked={!!sel[s.id]} onChange={() => toggle(s.id)} data-testid={`field-swms-check-${s.id}`} className="mt-1 accent-gold-500 h-4 w-4" />
                <button onClick={() => setOpen(open === s.id ? null : s.id)} className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-2">
                    {s.high_risk ? <ShieldAlert className="h-3.5 w-3.5 text-red-400 shrink-0" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-400 shrink-0" />}
                    <span className="text-sm font-medium truncate">{s.title}</span>
                  </div>
                  {s.high_risk && <Mono className="text-[0.5rem] text-red-300/80 mt-0.5 block">High-risk construction work</Mono>}
                </button>
              </div>
              {open === s.id && s.content && (
                <div className="px-3.5 pb-3.5 text-white/60 text-xs whitespace-pre-wrap border-t border-gold-500/10 pt-2" data-testid={`field-swms-content-${s.id}`}>
                  {s.content}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Btn onClick={acknowledge} disabled={busy || chosen.length === 0} testid="field-swms-ack">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Acknowledge {chosen.length || ""} SWMS
      </Btn>
    </div>
  );
}
